import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { tokens } from "../../theme";
import { motion } from "framer-motion";
import TopBarOfMarketing from "../../components/TopBarOfMarketing";
import Footer from "../../components/Footer";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TimerIcon from "@mui/icons-material/Timer";
import TenantService from "../../services/tenantService";

export default function TenantVerifyOtp({ darkMode, setDarkMode }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const location = useLocation();
  const tenantService = new TenantService();
  
  const email = location.state?.email;
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(180); // 3 dakika = 180 saniye
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Email yoksa geri yönlendir
  useEffect(() => {
    if (!email) {
      navigate("/forgot-password/tenant");
    }
  }, [email, navigate]);

  // Timer
  useEffect(() => {
    if (timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // Validation
    if (!otp || !otp.trim()) {
      setError("Please enter the verification code");
      return;
    }
    
    if (otp.length !== 6) {
      setError("Verification code must be 6 digits");
      return;
    }

    if (timeLeft <= 0) {
      setError("Verification code has expired. Please request a new code");
      return;
    }
    
    try {
      setLoading(true);
      const response = await tenantService.verifyPasswordOtp({ email, otp });
      
      setSnackbar({
        open: true,
        message: "Code verified! Redirecting...",
        severity: "success",
      });
      
      // 1 saniye sonra şifre sıfırlama sayfasına yönlendir
      setTimeout(() => {
        navigate("/reset-password/tenant", { 
          state: { resetToken: response.resetToken, email } 
        });
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.error || "An error occurred while verifying code");
      setSnackbar({
        open: true,
        message: err.response?.data?.error || "Invalid code",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      setLoading(true);
      await tenantService.requestPasswordOtp(email);
      setTimeLeft(180); // Reset timer
      setOtp(""); // Clear OTP input
      setSnackbar({
        open: true,
        message: "New code sent!",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "An error occurred while sending code",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      width: "100%",
      background: darkMode ? colors.primary[500] : 'white',
      color: darkMode ? 'white' : colors.grey[100],
    }}>
      <TopBarOfMarketing darkMode={darkMode} setDarkMode={setDarkMode} />
      <Box
        sx={{
          minHeight: "100vh",
          background: darkMode 
            ? `linear-gradient(135deg, ${colors.primary[600]}, ${colors.greenAccent[700]})`
            : `linear-gradient(135deg, ${colors.primary[400]}, ${colors.greenAccent[500]})`,
          display: "flex",
          flexDirection: "column",
          mt: "64px",
        }}
      >
        <Container maxWidth="sm" sx={{ py: 8, flex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Paper
              elevation={3}
              sx={{
                p: 4,
                borderRadius: "24px",
                background: darkMode ? colors.primary[400] : "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                color: darkMode ? 'white' : colors.grey[100],
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                },
              }}
            >
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/forgot-password/tenant")}
                sx={{
                  mb: 2,
                  color: darkMode ? "rgba(255, 255, 255, 0.9)" : "rgba(255, 255, 255, 0.95)",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: "white",
                  },
                }}
              >
                Go Back
              </Button>

              <Typography
                variant="h3"
                sx={{
                  textAlign: "center",
                  mb: 2,
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${colors.blueAccent[500]}, ${colors.greenAccent[400]})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Verification Code
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  textAlign: "center",
                  mb: 3,
                  color: darkMode ? colors.grey[300] : colors.grey[600],
                }}
              >
                Enter the 6-digit code sent to {email}
              </Typography>

              {/* Timer */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  mb: 3,
                  p: 2,
                  borderRadius: "12px",
                  background: timeLeft <= 30 
                    ? `linear-gradient(135deg, ${colors.redAccent[600]}, ${colors.redAccent[500]})`
                    : `linear-gradient(135deg, ${colors.blueAccent[600]}, ${colors.greenAccent[600]})`,
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
                }}
              >
                <TimerIcon sx={{ color: "white", fontSize: 28 }} />
                <Typography
                  variant="h4"
                  sx={{ 
                    color: "white", 
                    fontWeight: 700,
                    textShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  }}
                >
                  {formatTime(timeLeft)}
                </Typography>
              </Box>

              <form onSubmit={handleSubmit}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, color: darkMode ? "white" : colors.grey[900] }}>
                    Verification Code
                  </Typography>
                  <TextField
                    fullWidth
                    required
                    value={otp}
                    placeholder="000000"
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      if (value.length <= 6) {
                        setOtp(value);
                      }
                    }}
                    inputProps={{
                      maxLength: 6,
                      style: { 
                        textAlign: "center", 
                        fontSize: "2rem", 
                        letterSpacing: "0.5rem",
                        fontWeight: 600,
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        "&:hover fieldset": {
                          borderColor: colors.blueAccent[500],
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: darkMode ? "white" : colors.grey[900],
                      },
                    }}
                  />
                </Box>

                {error && (
                  <Typography
                    color="error"
                    variant="body2"
                    sx={{ mb: 2, textAlign: "center" }}
                  >
                    {error}
                  </Typography>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading || timeLeft <= 0}
                  sx={{
                    background: "white",
                    color: colors.greenAccent[600],
                    borderRadius: "12px",
                    padding: "12px",
                    fontSize: "1rem",
                    fontWeight: 600,
                    textTransform: "none",
                    mb: 2,
                    boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
                    "&:hover": {
                      background: "rgba(255,255,255,0.95)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                    },
                    "&:disabled": {
                      background: colors.grey[500],
                      color: colors.grey[300],
                    },
                  }}
                >
                  {loading ? "Verifying..." : "Verify Code"}
                </Button>

                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="body2" sx={{ color: darkMode ? colors.grey[300] : colors.grey[600], mb: 1 }}>
                    Didn't receive the code?
                  </Typography>
                  <Button
                    variant="text"
                    onClick={handleResend}
                    disabled={loading || timeLeft > 0}
                    sx={{
                      color: "rgba(255, 255, 255, 0.9)",
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        color: "white",
                      },
                      "&:disabled": {
                        color: colors.grey[500],
                      },
                    }}
                  >
                    Resend Code
                  </Button>
                </Box>
              </form>
            </Paper>
          </motion.div>
        </Container>

        <Footer />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            sx={{
              width: "100%",
              borderRadius: "12px",
            }}
          >
            <Typography variant="body1" color="white">
              {snackbar.message}
            </Typography>
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}








